/**
 * PageBackLink — universal back-navigation.
 *
 * Every public/user-facing page must accept `<PageBackLink />` at the top so
 * users never dead-end. Uses browser history when available; falls back to a
 * fixed home path.
 *
 * Props
 *   fallbackTo (string, default "/") — where to send the user if there is
 *                                       no meaningful history (opened from
 *                                       an SMS deep link, refresh, etc.)
 *   label      (string, default "Back") — link text
 *   style      (object)                 — extra inline styles for placement
 */

import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

export default function PageBackLink({ fallbackTo = "/", label = "Back", style = {} }) {
  const navigate = useNavigate();

  const onClick = (e) => {
    e.preventDefault();
    // History.length is a rough proxy for "did the user actually navigate here?"
    // Two states = ["initial", "current"] which usually means direct entry.
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate(fallbackTo);
    }
  };

  return (
    <div
      style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: "16px 24px 0",
        ...style,
      }}
      data-testid="page-back-link"
    >
      <Link
        to={fallbackTo}
        onClick={onClick}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 12px",
          borderRadius: 999,
          background: "rgba(15,23,42,0.06)",
          color: "#0F172A",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.02em",
          textDecoration: "none",
          border: "1px solid rgba(15,23,42,0.10)",
        }}
      >
        <ArrowLeft size={14} weight="bold" />
        {label}
      </Link>
    </div>
  );
}
